import type { ServerRow, SyncTable } from '@worker/sync/types'

/**
 * THE ONLY PLACE THAT KNOWS BOTH SPELLINGS.
 *
 * camelCase in TypeScript, snake_case in SQL -- CLAUDE.md rule 10. Every
 * other module speaks one dialect and is unaware the other exists. Scatter
 * this conversion and you get a codebase where nobody can be sure which form
 * a given variable holds.
 *
 * `_dirty` is local bookkeeping and is stripped on the way out; it must never
 * reach the server.
 */

/** Local table name to SQL table name. */
const TABLE_TO_SQL = {
  categories: 'categories',
  eventTypes: 'event_types',
  objectGroups: 'object_groups',
  eventObjects: 'event_objects',
  entries: 'entries',
  stampers: 'stampers',
  reminders: 'reminders',
  goals: 'goals',
  daySummaries: 'day_summaries',
  settings: 'settings',
} as const satisfies Record<string, SyncTable>

export type LocalTable = keyof typeof TABLE_TO_SQL

export const LOCAL_TABLES = Object.keys(TABLE_TO_SQL) as LocalTable[]

const SQL_TO_TABLE = Object.fromEntries(
  Object.entries(TABLE_TO_SQL).map(([local, sql]) => [sql, local]),
) as Record<SyncTable, LocalTable>

export function toSqlTable(table: LocalTable): SyncTable {
  return TABLE_TO_SQL[table]
}

export function toLocalTable(table: SyncTable): LocalTable | undefined {
  return SQL_TO_TABLE[table]
}

export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

export function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())
}

/** Local row to wire row. Drops `_dirty`. */
export function serializeRow(row: Record<string, unknown>): ServerRow {
  const out: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(row)) {
    if (key === '_dirty') continue
    out[camelToSnake(key)] = value === undefined ? null : value
  }

  return out as ServerRow
}

/**
 * Wire row to local row.
 *
 * `_dirty` is set to 0: a row that just came from the server is by definition
 * in sync with it. The caller decides whether to keep a local edit instead --
 * see syncEngine's applyRemoteChanges.
 */
export function deserializeRow(row: ServerRow): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(row)) {
    out[snakeToCamel(key)] = value
  }

  out['_dirty'] = 0
  return out
}

export function serializeRows(rows: readonly Record<string, unknown>[]): ServerRow[] {
  return rows.map(serializeRow)
}

export function deserializeRows(rows: readonly ServerRow[]): Record<string, unknown>[] {
  return rows.map(deserializeRow)
}
